'use client'

import type { ComponentType, SVGProps } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgBatteryBolt from '@/icons/regular/BatteryBolt'
import SvgBluetooth from '@/icons/regular/Bluetooth'
import SvgDroplet from '@/icons/regular/Droplet'
import SvgMagnet from '@/icons/regular/Magnet'
import type { DirectoryRow } from '@/lib/directory/row'

type SpecIcon = ComponentType<SVGProps<SVGSVGElement>>

interface SpecChip {
  key: string
  label: string
  Icon: SpecIcon
}

interface RowSpecsProps {
  specs: DirectoryRow['products'][number]['specs'] | undefined
}

// Capabilities belong to the pictured model, so a CIC without a telecoil
// never inherits its RIC sibling's features. Bluetooth and IP codes are not
// translated.
export const RowSpecs = ({ specs }: RowSpecsProps) => {
  const { t } = useTranslation()
  if (!specs) {
    return null
  }
  const chips: SpecChip[] = []
  if (specs.rechargeable) {
    chips.push({ key: 'rechargeable', label: t('directory.spec.rechargeable', 'Rechargeable'), Icon: SvgBatteryBolt })
  }
  if (specs.bluetooth) {
    chips.push({ key: 'bluetooth', label: 'Bluetooth', Icon: SvgBluetooth })
  }
  if (specs.telecoil) {
    chips.push({ key: 'telecoil', label: t('directory.spec.telecoil', 'Telecoil'), Icon: SvgMagnet })
  }
  if (specs.ip) {
    chips.push({ key: 'ip', label: specs.ip, Icon: SvgDroplet })
  }

  if (chips.length === 0) {
    return null
  }

  return (
    <ul className="text-navy/80 flex flex-wrap gap-x-4 gap-y-2 text-[13px] leading-snug">
      {chips.map(({ key, label, Icon }) => (
        <li key={key} className="inline-flex items-center gap-1.5 font-medium">
          <Icon aria-hidden className="fill-navy/55 h-3.5 w-3.5 shrink-0" />
          {label}
        </li>
      ))}
    </ul>
  )
}
